import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { API_BASE_URL } from './config';

// ── Constants ──────────────────────────────────────────────────────────────────

const TEMPLATE_CATEGORIES = [
  'All', 'Email Automation', 'Data Sync', 'AI Pipelines', 'DevOps', 'Productivity',
  'Marketing', 'CRM', 'Finance', 'Security',
];

const PLUGIN_CATEGORIES = [
  'All', 'Document Processing', 'Utilities', 'Media', 'Data Processing',
  'Data Sources', 'Messaging',
];

// Phase L5 — Case Client Layout templates ship under verticals (Insurance,
// Legal, HR, …) plus an "All" pseudo-filter. The list is short enough that
// we hard-code the verticals here; new ones added on the backend show up
// automatically via the seed's `vertical` field, falling into "Other".
const LAYOUT_CATEGORIES = ['All', 'Insurance', 'Legal', 'HR', 'Other'];

const SORT_OPTIONS = [
  { value: 'installs', label: '⬇ Most Installs' },
  { value: 'rating', label: '⭐ Highest Rated' },
  { value: 'name', label: '🔤 A–Z' },
];

const TRUST_COLORS = {
  high: { bg: '#22c55e22', border: '#22c55e', text: '#22c55e' },
  medium: { bg: '#f59e0b22', border: '#f59e0b', text: '#f59e0b' },
  low: { bg: '#ef444422', border: '#ef4444', text: '#ef4444' },
};

// ── Style helpers ──────────────────────────────────────────────────────────────

const st = {
  page: {
    width: '100vw', minHeight: '100vh',
    background: 'var(--bg-primary)', color: 'var(--text-primary)',
    display: 'flex', flexDirection: 'column',
  },
  header: {
    display: 'flex', alignItems: 'center', gap: '16px',
    padding: '20px 32px', borderBottom: '1px solid var(--border-color)',
    background: 'var(--bg-secondary)',
  },
  backBtn: {
    background: 'none', border: '1px solid var(--border-color)',
    color: 'var(--text-secondary)', padding: '6px 14px', borderRadius: '6px',
    cursor: 'pointer', fontSize: '13px',
  },
  title: { margin: 0, fontSize: '20px', fontWeight: '700', color: 'var(--text-primary)' },
  subtitle: { margin: 0, fontSize: '13px', color: 'var(--text-tertiary)' },
  body: { flex: 1, padding: '28px 32px', maxWidth: '1200px', margin: '0 auto', width: '100%' },

  tabs: { display: 'flex', gap: '4px', marginBottom: '24px', borderBottom: '1px solid var(--border-color)', paddingBottom: '0' },
  tab: (active) => ({
    padding: '10px 20px', border: 'none', background: 'none', cursor: 'pointer',
    fontSize: '14px', fontWeight: active ? '600' : '400',
    color: active ? 'var(--accent-color)' : 'var(--text-secondary)',
    borderBottom: active ? '2px solid var(--accent-color)' : '2px solid transparent',
    marginBottom: '-1px', transition: 'all 0.15s',
  }),

  toolbar: {
    display: 'flex', gap: '12px', marginBottom: '20px', flexWrap: 'wrap', alignItems: 'center',
  },
  searchBox: {
    flex: '1 1 220px', padding: '8px 12px', borderRadius: '6px',
    border: '1px solid var(--border-color)', background: 'var(--bg-secondary)',
    color: 'var(--text-primary)', fontSize: '13px', minWidth: '180px',
  },
  select: {
    padding: '8px 12px', borderRadius: '6px',
    border: '1px solid var(--border-color)', background: 'var(--bg-secondary)',
    color: 'var(--text-primary)', fontSize: '13px', cursor: 'pointer',
  },

  catRow: { display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '20px' },
  catBtn: (active) => ({
    padding: '4px 12px', borderRadius: '12px', fontSize: '12px', cursor: 'pointer',
    border: `1px solid ${active ? 'var(--accent-color)' : 'var(--border-color)'}`,
    background: active ? 'var(--accent-color)22' : 'var(--bg-secondary)',
    color: active ? 'var(--accent-color)' : 'var(--text-secondary)',
    fontWeight: active ? '600' : '400',
  }),

  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(310px, 1fr))', gap: '16px' },

  card: {
    background: 'var(--bg-secondary)', border: '1px solid var(--border-color)',
    borderRadius: '10px', padding: '18px', display: 'flex', flexDirection: 'column', gap: '10px',
    transition: 'border-color 0.15s, box-shadow 0.15s',
  },
  cardHover: { borderColor: 'var(--accent-color)', boxShadow: '0 0 0 2px var(--accent-color)22' },

  cardHeader: { display: 'flex', alignItems: 'flex-start', gap: '12px' },
  iconBox: {
    width: '40px', height: '40px', borderRadius: '8px',
    background: 'var(--bg-tertiary)', display: 'flex', alignItems: 'center',
    justifyContent: 'center', fontSize: '20px', flexShrink: 0,
  },
  cardName: { margin: 0, fontSize: '15px', fontWeight: '600', color: 'var(--text-primary)' },
  cardDesc: { margin: 0, fontSize: '12px', color: 'var(--text-secondary)', lineHeight: '1.5' },
  cardMeta: { display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' },
  metaChip: (color) => ({
    fontSize: '11px', padding: '2px 8px', borderRadius: '10px',
    background: color + '22', color: color, border: `1px solid ${color}`,
  }),
  tagRow: { display: 'flex', gap: '6px', flexWrap: 'wrap' },
  tag: {
    fontSize: '11px', padding: '2px 8px', borderRadius: '10px',
    background: 'var(--bg-tertiary)', color: 'var(--text-tertiary)',
    border: '1px solid var(--border-color)',
  },
  cardFooter: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 'auto', paddingTop: '8px' },
  installBtn: (installing) => ({
    padding: '7px 16px', borderRadius: '6px', border: 'none', cursor: installing ? 'not-allowed' : 'pointer',
    background: installing ? 'var(--bg-tertiary)' : 'var(--accent-color)',
    color: installing ? 'var(--text-tertiary)' : '#fff',
    fontSize: '12px', fontWeight: '600',
  }),
  statsRow: { display: 'flex', gap: '12px', alignItems: 'center' },
  statItem: { fontSize: '11px', color: 'var(--text-tertiary)' },

  // Detail drawer
  drawer: {
    position: 'fixed', top: 0, right: 0, bottom: 0, width: '420px',
    background: 'var(--bg-secondary)', borderLeft: '1px solid var(--border-color)',
    zIndex: 1000, display: 'flex', flexDirection: 'column', overflowY: 'auto',
    boxShadow: '-4px 0 24px rgba(0,0,0,0.3)',
  },
  drawerHeader: {
    padding: '20px 24px', borderBottom: '1px solid var(--border-color)',
    display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
  },
  drawerBody: { padding: '20px 24px', flex: 1 },
  closeBtn: {
    background: 'none', border: 'none', color: 'var(--text-tertiary)',
    fontSize: '20px', cursor: 'pointer', lineHeight: 1,
  },
  readmePre: {
    background: 'var(--bg-tertiary)', padding: '12px', borderRadius: '6px',
    fontSize: '12px', whiteSpace: 'pre-wrap', color: 'var(--text-secondary)',
    lineHeight: '1.6', border: '1px solid var(--border-color)', overflowX: 'auto',
  },

  // Rating dialog
  overlay: {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000,
  },
  ratingModal: {
    background: 'var(--bg-secondary)', border: '1px solid var(--border-color)',
    borderRadius: '10px', padding: '28px', width: '340px', display: 'flex',
    flexDirection: 'column', gap: '16px',
  },

  msg: (type) => ({
    padding: '10px 16px', borderRadius: '6px', fontSize: '13px',
    background: type === 'success' ? '#22c55e22' : '#ef444422',
    color: type === 'success' ? '#22c55e' : '#ef4444',
    border: `1px solid ${type === 'success' ? '#22c55e' : '#ef4444'}`,
    marginBottom: '12px',
  }),

  emptyState: {
    textAlign: 'center', padding: '60px 20px',
    color: 'var(--text-tertiary)', fontSize: '14px',
  },
};

// ── Star Rating Component ──────────────────────────────────────────────────────

function StarRating({ value, onChange, size = 24 }) {
  const [hovered, setHovered] = useState(0);
  return (
    <div style={{ display: 'flex', gap: '4px' }}>
      {[1, 2, 3, 4, 5].map(n => (
        <span
          key={n}
          onClick={() => onChange && onChange(n)}
          onMouseEnter={() => setHovered(n)}
          onMouseLeave={() => setHovered(0)}
          style={{
            fontSize: size, cursor: onChange ? 'pointer' : 'default',
            color: n <= (hovered || value) ? '#f59e0b' : 'var(--border-color)',
            transition: 'color 0.1s',
          }}
        >★</span>
      ))}
    </div>
  );
}

// ── Stars display (read-only) ──────────────────────────────────────────────────

function Stars({ avg, count }) {
  if (!avg) return <span style={{ fontSize: '11px', color: 'var(--text-tertiary)' }}>No ratings yet</span>;
  return (
    <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
      <span style={{ color: '#f59e0b' }}>★</span>{' '}
      {avg.toFixed(1)} <span style={{ color: 'var(--text-tertiary)' }}>({count})</span>
    </span>
  );
}

// ── Trust Score Badge ──────────────────────────────────────────────────────────

function TrustBadge({ score }) {
  if (score == null) return null;
  const tier = score >= 90 ? 'high' : score >= 70 ? 'medium' : 'low';
  const label = tier === 'high' ? '✅ Verified' : tier === 'medium' ? '⚠️ Review' : '⛔ Caution';
  const c = TRUST_COLORS[tier];
  return (
    <span style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '10px', background: c.bg, color: c.text, border: `1px solid ${c.border}` }}>
      {label} · {score}
    </span>
  );
}

// ── Rating Dialog ──────────────────────────────────────────────────────────────

function RatingDialog({ item, itemType, myRatings, onClose, onSubmit }) {
  const existing = myRatings[item.id];
  const [rating, setRating] = useState(existing?.rating || 0);
  const [review, setReview] = useState(existing?.review || '');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!rating) return;
    setSubmitting(true);
    try {
      await onSubmit(item.id, itemType, rating, review);
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={st.overlay} onClick={onClose}>
      <div style={st.ratingModal} onClick={e => e.stopPropagation()}>
        <div>
          <h3 style={{ margin: '0 0 4px', fontSize: '16px' }}>Rate: {item.name}</h3>
          <p style={{ margin: 0, fontSize: '12px', color: 'var(--text-tertiary)' }}>
            Your rating helps other users choose the right templates and plugins.
          </p>
        </div>
        <StarRating value={rating} onChange={setRating} size={28} />
        <textarea
          value={review}
          onChange={e => setReview(e.target.value)}
          placeholder="Write a short review (optional)..."
          rows={4}
          style={{
            width: '100%', padding: '8px', borderRadius: '6px', resize: 'vertical',
            background: 'var(--bg-tertiary)', border: '1px solid var(--border-color)',
            color: 'var(--text-primary)', fontSize: '13px', boxSizing: 'border-box',
          }}
        />
        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{ ...st.installBtn(false), background: 'var(--bg-tertiary)', color: 'var(--text-secondary)' }}>
            Cancel
          </button>
          <button onClick={handleSubmit} disabled={!rating || submitting} style={st.installBtn(!rating || submitting)}>
            {submitting ? 'Submitting…' : 'Submit Rating'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Detail Drawer ──────────────────────────────────────────────────────────────

function DetailDrawer({ item, itemType, onClose, onInstall, onRate, installing, myRatings, isAdmin, currentWorkspace }) {
  const canInstallPlugin = itemType === 'plugin' && isAdmin;
  const canInstallTemplate = itemType === 'template';
  const canInstallLayout = itemType === 'layout_template' && !!currentWorkspace?.id;
  const showInstall = canInstallPlugin || canInstallTemplate || canInstallLayout;

  return (
    <div style={st.drawer}>
      <div style={st.drawerHeader}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
          <div style={st.iconBox}>{item.icon || '🧩'}</div>
          <div>
            <h3 style={{ margin: '0 0 4px', fontSize: '16px' }}>{item.name}</h3>
            <Stars avg={item.avg_rating} count={item.rating_count} />
          </div>
        </div>
        <button onClick={onClose} style={st.closeBtn}>✕</button>
      </div>
      <div style={st.drawerBody}>
        <p style={{ fontSize: '13px', color: 'var(--text-secondary)', lineHeight: '1.6', marginTop: 0 }}>
          {item.description}
        </p>

        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '16px' }}>
          {item.trust_score != null && <TrustBadge score={item.trust_score} />}
          {item.version && <span style={st.metaChip('var(--accent-color)')}>v{item.version}</span>}
          {item.license && <span style={st.tag}>{item.license}</span>}
          {item.category && <span style={st.tag}>{item.category}</span>}
        </div>

        <div style={{ display: 'flex', gap: '12px', marginBottom: '16px', fontSize: '12px', color: 'var(--text-tertiary)' }}>
          <span>📥 {item.installs ?? 0} installs</span>
          {item.author && <span>👤 {item.author}</span>}
        </div>

        {(item.tags || []).length > 0 && (
          <div style={{ ...st.tagRow, marginBottom: '16px' }}>
            {(item.tags || []).map(t => <span key={t} style={st.tag}>{t}</span>)}
          </div>
        )}

        {item.readme && (
          <div style={{ marginBottom: '16px' }}>
            <h4 style={{ margin: '0 0 8px', fontSize: '13px', color: 'var(--text-primary)' }}>README</h4>
            <pre style={st.readmePre}>{item.readme}</pre>
          </div>
        )}

        {item.required_credentials && (
          <div style={{ marginBottom: '16px' }}>
            <h4 style={{ margin: '0 0 8px', fontSize: '13px' }}>Required Credentials</h4>
            <div style={st.tagRow}>
              {item.required_credentials.map(c => <span key={c} style={{ ...st.tag, color: 'var(--warning-color)' }}>{c}</span>)}
            </div>
          </div>
        )}

        <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
          {showInstall && (
            <button
              onClick={() => onInstall(item.id)}
              disabled={installing === item.id}
              style={st.installBtn(installing === item.id)}
            >
              {installing === item.id ? 'Installing…' : '📥 Install'}
            </button>
          )}
          {itemType === 'plugin' && !isAdmin && (
            <span style={{ fontSize: '12px', color: 'var(--text-tertiary)', alignSelf: 'center' }}>
              Admin access required to install plugins
            </span>
          )}
          <button onClick={() => onRate(item)} style={{ ...st.installBtn(false), background: 'var(--bg-tertiary)', color: 'var(--text-secondary)' }}>
            ⭐ {myRatings[item.id] ? 'Update Rating' : 'Rate'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Item Card ──────────────────────────────────────────────────────────────────

function ItemCard({ item, itemType, onInstall, onRate, onSelect, installing, myRatings, isAdmin, disabledReason }) {
  const [hovered, setHovered] = useState(false);
  const canInstall = itemType === 'template'
    || (itemType === 'plugin' && isAdmin)
    || (itemType === 'layout_template' && !disabledReason);
  const myRating = myRatings[item.id];

  return (
    <div
      style={{ ...st.card, ...(hovered ? st.cardHover : {}) }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div style={st.cardHeader} onClick={() => onSelect(item)} role="button" tabIndex={0} onKeyDown={e => e.key === 'Enter' && onSelect(item)}>
        <div style={st.iconBox}>{item.icon || '🧩'}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={st.cardName}>{item.name}</p>
          <Stars avg={item.avg_rating} count={item.rating_count} />
        </div>
      </div>

      <p style={st.cardDesc}>{item.description}</p>

      <div style={st.cardMeta}>
        {item.trust_score != null && <TrustBadge score={item.trust_score} />}
        {item.version && <span style={st.metaChip('var(--accent-color)')}>v{item.version}</span>}
        {item.category && <span style={st.tag}>{item.category}</span>}
      </div>

      {(item.tags || []).length > 0 && (
        <div style={st.tagRow}>
          {(item.tags || []).slice(0, 4).map(t => <span key={t} style={st.tag}>{t}</span>)}
        </div>
      )}

      <div style={st.cardFooter}>
        <div style={st.statsRow}>
          <span style={st.statItem}>📥 {item.installs ?? 0}</span>
          {item.author && <span style={st.statItem}>👤 {item.author || 'Community'}</span>}
          {myRating && <span style={{ ...st.statItem, color: '#f59e0b' }}>★ {myRating.rating} (my rating)</span>}
        </div>
        <div style={{ display: 'flex', gap: '6px' }}>
          <button
            onClick={e => { e.stopPropagation(); onRate(item); }}
            title="Rate this item"
            style={{ ...st.installBtn(false), background: 'var(--bg-tertiary)', color: 'var(--text-tertiary)', padding: '6px 10px' }}
          >⭐</button>
          {canInstall && (
            <button
              onClick={e => { e.stopPropagation(); onInstall(item.id); }}
              disabled={installing === item.id}
              style={st.installBtn(installing === item.id)}
            >
              {installing === item.id ? '…' : '📥 Install'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Main MarketplaceModal Component ───────────────────────────────────────────

export default function MarketplaceModal({ onBack, isAdmin, currentWorkspace }) {
  const [tab, setTab] = useState('templates');
  const [templates, setTemplates] = useState([]);
  const [plugins, setPlugins] = useState([]);
  const [layoutSeeds, setLayoutSeeds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState(null);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('All');
  const [sort, setSort] = useState('installs');
  const [installing, setInstalling] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [ratingItem, setRatingItem] = useState(null);
  const [myRatings, setMyRatings] = useState({});

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const showMsg = (type, text) => {
    setMsg({ type, text });
    setTimeout(() => setMsg(null), 5000);
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [tRes, pRes, rRes, lRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/marketplace/templates`, { headers }),
        axios.get(`${API_BASE_URL}/marketplace/plugins`, { headers }),
        axios.get(`${API_BASE_URL}/marketplace/my-ratings`, { headers }),
        axios.get(`${API_BASE_URL}/marketplace/layout-templates`, { headers }).catch(() => ({ data: [] })),
      ]);
      setTemplates(tRes.data || []);
      setPlugins(pRes.data || []);
      setMyRatings(rRes.data || {});
      setLayoutSeeds(Array.isArray(lRes.data) ? lRes.data : []);
    } catch {
      showMsg('error', 'Failed to load marketplace data.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Reset category when switching tabs
  useEffect(() => { setCategory('All'); setSearch(''); }, [tab]);

  const categories = tab === 'templates' ? TEMPLATE_CATEGORIES
    : tab === 'plugins' ? PLUGIN_CATEGORIES
    : LAYOUT_CATEGORIES;
  const items = tab === 'templates' ? templates
    : tab === 'plugins' ? plugins
    : layoutSeeds;

  const filtered = useMemo(() => {
    let list = [...items];
    if (category !== 'All') {
      if (tab === 'layouts') {
        const wanted = category.toLowerCase();
        list = list.filter(i => (i.vertical || 'other').toLowerCase() === wanted);
      } else {
        list = list.filter(i => i.category === category);
      }
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(i =>
        i.name?.toLowerCase().includes(q) ||
        i.description?.toLowerCase().includes(q) ||
        (i.tags || []).some(t => t.toLowerCase().includes(q))
      );
    }
    if (sort === 'installs') list.sort((a, b) => (b.installs ?? 0) - (a.installs ?? 0));
    else if (sort === 'rating') list.sort((a, b) => (b.avg_rating ?? 0) - (a.avg_rating ?? 0));
    else list.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    return list;
  }, [items, category, search, sort]);

  const handleInstall = async (itemId) => {
    setInstalling(itemId);
    try {
      let url;
      let body = {};
      if (tab === 'templates') {
        url = `${API_BASE_URL}/marketplace/templates/${itemId}/install`;
      } else if (tab === 'plugins') {
        url = `${API_BASE_URL}/marketplace/plugins/${itemId}/install`;
      } else {
        // layouts — must target a workspace
        if (!currentWorkspace?.id) {
          showMsg('error', 'Pick a workspace before installing a layout template.');
          setInstalling(null);
          return;
        }
        url = `${API_BASE_URL}/marketplace/layout-templates/${itemId}/install`;
        body = { workspace_id: currentWorkspace.id };
      }
      const res = await axios.post(url, body, { headers });
      if (tab === 'templates') {
        showMsg('success', `✅ Template installed as workflow "${res.data.name}". Find it in your workflow list.`);
      } else if (tab === 'plugins') {
        showMsg('success', `✅ Plugin "${res.data.node_id}" installed. It will be available in the node sidebar.`);
      } else {
        showMsg('success', `✅ Layout "${res.data.name}" installed as draft. Open Case Type Admin → Layouts to publish.`);
      }
      fetchData();
    } catch (err) {
      showMsg('error', err.response?.data?.detail || 'Installation failed.');
    } finally {
      setInstalling(null);
    }
  };

  const handleRate = async (itemId, itemType, rating, review) => {
    await axios.post(`${API_BASE_URL}/marketplace/rate`, { item_id: itemId, item_type: itemType, rating, review }, { headers });
    showMsg('success', '⭐ Rating submitted. Thank you!');
    fetchData();
  };

  return (
    <div style={st.page}>
      {/* Header */}
      <div style={st.header}>
        <button onClick={onBack} style={st.backBtn}>← Back</button>
        <div>
          <h1 style={st.title}>🛒 Community Marketplace</h1>
          <p style={st.subtitle}>Browse and install community templates and plugins for xavvy</p>
        </div>
      </div>

      <div style={{ ...st.body, boxSizing: 'border-box' }}>
        {msg && <div style={st.msg(msg.type)}>{msg.text}</div>}

        {/* Tabs */}
        <div style={st.tabs}>
          <button style={st.tab(tab === 'templates')} onClick={() => setTab('templates')}>
            📋 Templates ({templates.length})
          </button>
          <button style={st.tab(tab === 'plugins')} onClick={() => setTab('plugins')}>
            🧩 Plugins ({plugins.length})
          </button>
          <button style={st.tab(tab === 'layouts')} onClick={() => setTab('layouts')}>
            🧱 Layouts ({layoutSeeds.length})
          </button>
        </div>

        {/* Toolbar */}
        <div style={st.toolbar}>
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder={`Search ${tab}…`}
            style={st.searchBox}
          />
          <select value={sort} onChange={e => setSort(e.target.value)} style={st.select}>
            {SORT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>

        {/* Category pills */}
        <div style={st.catRow}>
          {categories.map(c => (
            <button key={c} onClick={() => setCategory(c)} style={st.catBtn(category === c)}>{c}</button>
          ))}
        </div>

        {/* Content */}
        {loading ? (
          <div style={st.emptyState}>Loading marketplace…</div>
        ) : filtered.length === 0 ? (
          <div style={st.emptyState}>
            <div style={{ fontSize: '40px', marginBottom: '12px' }}>🔍</div>
            <div>No {tab} found matching your filters.</div>
            <div style={{ marginTop: '8px', fontSize: '12px' }}>Try a different search or category.</div>
          </div>
        ) : (
          <div style={st.grid}>
            {filtered.map(item => (
              <ItemCard
                key={item.id}
                item={item}
                itemType={tab === 'templates' ? 'template' : tab === 'plugins' ? 'plugin' : 'layout_template'}
                onInstall={handleInstall}
                onRate={setRatingItem}
                onSelect={setSelectedItem}
                installing={installing}
                myRatings={myRatings}
                isAdmin={isAdmin}
                disabledReason={tab === 'layouts' && !currentWorkspace?.id ? 'Select a workspace to install layouts' : null}
              />
            ))}
          </div>
        )}

        {/* Plugin install info */}
        {tab === 'plugins' && !isAdmin && (
          <div style={{ marginTop: '20px', padding: '12px 16px', background: 'var(--bg-secondary)', borderRadius: '6px', border: '1px solid var(--border-color)', fontSize: '12px', color: 'var(--text-tertiary)' }}>
            🔒 Plugin installation requires admin access. Contact your workspace administrator to install plugins.
          </div>
        )}

        {/* Layout install info */}
        {tab === 'layouts' && !currentWorkspace?.id && (
          <div style={{ marginTop: '20px', padding: '12px 16px', background: 'var(--bg-secondary)', borderRadius: '6px', border: '1px solid var(--border-color)', fontSize: '12px', color: 'var(--text-tertiary)' }}>
            🧱 Layout templates install into a specific workspace. Pick a workspace from the top bar before installing.
          </div>
        )}
      </div>

      {/* Detail Drawer */}
      {selectedItem && (
        <>
          <div
            style={{ position: 'fixed', inset: 0, zIndex: 999 }}
            onClick={() => setSelectedItem(null)}
          />
          <DetailDrawer
            item={selectedItem}
            itemType={tab === 'templates' ? 'template' : tab === 'plugins' ? 'plugin' : 'layout_template'}
            onClose={() => setSelectedItem(null)}
            onInstall={handleInstall}
            onRate={setRatingItem}
            installing={installing}
            myRatings={myRatings}
            isAdmin={isAdmin}
            currentWorkspace={currentWorkspace}
          />
        </>
      )}

      {/* Rating Dialog */}
      {ratingItem && (
        <RatingDialog
          item={ratingItem}
          itemType={tab === 'templates' ? 'template' : tab === 'plugins' ? 'plugin' : 'layout_template'}
          myRatings={myRatings}
          onClose={() => setRatingItem(null)}
          onSubmit={handleRate}
        />
      )}
    </div>
  );
}
